// The Asana import's rules, kept apart from the screen: what each line resolves to from the
// mapping and its own changes, what its subtasks are sent as, and the body one Import sends.
// Behaviour: web/docs/features/spark.md, "Importing from Asana".

import type {
  AsanaLine, AsanaLineImport, AsanaPerson, AsanaPreview, AsanaSubtask, AsanaTaskImport, Person,
} from '../Spark/sparkApi';
import { SPECIALITIES, WORK_STEPS } from '../Spark/sparkRules';
import type { Speciality, Stage, TeamRole } from '../Spark/sparkRules';
import type {
  ImportDraft, LineDraft, LineState, MappingChoices, PickPerson, PlannedTask, RequiredField, Resolved,
  ResolvedLine, TaskKind, TokenMap,
} from './asanaImportTypes';

/** How the API names a blank Asana value. */
export const BLANK = '(blank)';
/** How the API names a line with no section. */
export const NO_SECTION = '(none)';
export const TEAM_ROLES: TeamRole[] = ['producer', 'designer', 'pilot'];
/** A subtask name repeating this often is offered as "leave out everywhere". */
export const REPEAT_AT = 10;
const NAME_MAX = 200;

export const emptyMapping = (): MappingChoices => ({
  pipeline: {}, status: {}, spice: {}, speciality: {}, noSpeciality: null, people: {}, leaveOut: [],
});

/** The mapping in force: each choice made, else the API's suggestion. */
export interface EffectiveMapping {
  pipeline: Record<string, Stage | null>;
  status: Record<string, string | null>;
  spice: Record<string, number | null>;
  speciality: Record<string, TokenMap>;
  noSpeciality: Speciality | null;
  people: Record<string, string | null>;
  leaveOut: Set<string>;
}

const has = (obj: unknown, key: string): boolean =>
  !!obj && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key)
  && (obj as Record<string, unknown>)[key] !== undefined;

export const tokenFromSuggest = (s: AsanaPreview['values']['speciality'][number]['suggest']): TokenMap => {
  if (s && 'speciality' in s) return { kind: 'speciality', speciality: s.speciality };
  if (s && 'work_steps' in s) return { kind: 'steps', steps: s.work_steps.filter((w) => WORK_STEPS.includes(w)) };
  return { kind: 'ignore' };
};

export function effectiveMapping(preview: AsanaPreview, m: MappingChoices): EffectiveMapping {
  const { values } = preview;
  const pick = <T, S>(rows: { value: string; suggest: S | null }[], chosen: Record<string, T>, fromSuggest: (s: S | null) => T) =>
    Object.fromEntries(rows.map((r) => [r.value, has(chosen, r.value) ? chosen[r.value] : fromSuggest(r.suggest)]));
  return {
    pipeline: pick(values.pipeline, m.pipeline, (s) => s ?? null),
    status: pick(values.status, m.status, (s) => s ?? null),
    spice: pick(values.spice, m.spice, (s) => s ?? null),
    speciality: pick(values.speciality.filter((r) => r.value !== BLANK), m.speciality, tokenFromSuggest),
    noSpeciality: m.noSpeciality ?? null,
    people: Object.fromEntries(values.people.map((p) => [p.key, has(m.people, p.key) ? m.people[p.key] : p.user?.id ?? null])),
    leaveOut: new Set(Array.isArray(m.leaveOut) ? m.leaveOut : []),
  };
}

/** The key People maps an Asana person by: the email, else the name. */
export const personKey = (p: AsanaPerson | null): string | null => (p ? p.email || p.name || null : null);

/** Spark's own eligibility for a team slot, as the Spark team panel applies it. */
export const canTake = (role: TeamRole, p: PickPerson | undefined): boolean => {
  if (!p || !p.inSpark || !p.active) return false;
  if (role === 'producer') return p.producer;
  if (role === 'pilot') return p.pilot;
  return p.roles.includes('designer') || p.roles.includes('director');
};

const unique = <T,>(xs: T[]): T[] => Array.from(new Set(xs));

/** What a line's own speciality tokens give, through the mapping. */
export function fromTokens(line: AsanaLine, eff: EffectiveMapping): { specialities: Speciality[]; steps: string[] } {
  const maps = line.asana.speciality.map((t) => eff.speciality[t]).filter(Boolean);
  const specialities = unique(maps.flatMap((m) => (m.kind === 'speciality' ? [m.speciality] : [])));
  const steps = unique(maps.flatMap((m) => (m.kind === 'steps' ? m.steps : [])));
  return { specialities, steps };
}

/** Whether Spark would let this person take this team slot. null until Spark's people have loaded. */
export type Eligible = ((role: TeamRole, userId: string) => boolean) | null;

export const eligibility = (people: Map<string, PickPerson>, loaded: boolean): Eligible =>
  (loaded ? (role, userId) => canTake(role, people.get(userId)) : null);

export function resolveLine(
  line: AsanaLine,
  draft: LineDraft | undefined,
  eff: EffectiveMapping,
  eligible: Eligible,
): ResolvedLine {
  const d: LineDraft = draft && typeof draft === 'object' ? draft : {};
  const own = <K extends keyof LineDraft, T>(key: K, fallback: Resolved<T>): Resolved<T> =>
    (has(d, key) ? { value: d[key] as unknown as T, source: 'line' } : fallback);

  const { specialities, steps } = fromTokens(line, eff);
  const specialityFallback: Resolved<Speciality | null> = specialities.length === 1
    ? { value: specialities[0], source: 'mapping' }
    : { value: specialities.length === 0 ? eff.noSpeciality : null, source: 'mapping' };
  const speciality = own('speciality', specialityFallback);
  const channel = own('channel', {
    value: speciality.value ? SPECIALITIES.find((s) => s.key === speciality.value)?.channel ?? null : null,
    source: 'rule',
  });

  const assigneeKey = personKey(line.assignee);
  const assigneeUser = assigneeKey ? eff.people[assigneeKey] ?? null : null;
  const assigneeRole = has(d, 'assignee_role') ? d.assignee_role : 'producer';
  const ineligible: Partial<Record<TeamRole, string>> = {};
  const team = {} as Record<TeamRole, Resolved<string | null>>;
  for (const role of TEAM_ROLES) {
    if (has(d.team, role)) {
      team[role] = { value: d.team[role] ?? null, source: 'line' };
    } else if (assigneeRole === role && assigneeUser) {
      // Spark refuses a producer without producer access, and so on; the import does not ask, so
      // the screen does. Until Spark's people have loaded, nobody is judged.
      const refused = !!eligible && !eligible(role, assigneeUser);
      if (refused) ineligible[role] = assigneeUser;
      team[role] = { value: refused ? null : assigneeUser, source: 'mapping' };
    } else {
      team[role] = { value: null, source: 'asana' };
    }
  }

  const name = own('name', { value: line.name, source: 'asana' });
  const stage = own('stage', { value: eff.pipeline[line.asana.pipeline ?? BLANK] ?? null, source: 'mapping' });
  const spice = own('spice', { value: eff.spice[line.asana.spice ?? BLANK] ?? null, source: 'mapping' });

  const trimmed = (name.value || '').trim();
  const missing: RequiredField[] = [];
  if (!trimmed || trimmed.length > NAME_MAX) missing.push('name');
  if (!channel.value) missing.push('channel');
  if (!speciality.value) missing.push('speciality');
  if (!stage.value) missing.push('stage');
  if (!spice.value) missing.push('spice');

  const eventId = has(d, 'event_id') ? d.event_id : null;
  const linkTo = has(d, 'link_to') ? d.link_to : null;

  return {
    name,
    channel,
    speciality,
    specialityClash: specialities.length > 1 ? specialities : [],
    work_steps: own('work_steps', { value: steps, source: 'mapping' }),
    stage,
    status_reason: own('status_reason', { value: eff.status[line.asana.status ?? BLANK] ?? null, source: 'mapping' }),
    spice,
    show_date: own('show_date', { value: line.asana.show_date, source: 'asana' }),
    preview_due: own('preview_due', { value: line.asana.delivery_date, source: 'asana' }),
    ls_drone_count: own('ls_drone_count', { value: line.asana.drones, source: 'asana' }),
    pyro_drone_count: own('pyro_drone_count', { value: line.asana.pyro, source: 'asana' }),
    client_name: own('client_name', { value: line.asana.client, source: 'asana' }),
    assignee_role: assigneeRole,
    assigneeUser,
    ineligible,
    team,
    // A draft can outlive the export it was made against: only what this line still offers counts.
    event_id: eventId && line.events.some((e) => e.id === eventId) ? eventId : null,
    draw: has(d, 'draw') ? !!d.draw : false,
    link_to: linkTo && line.same_name.some((p) => p.id === linkTo) ? linkTo : null,
    missing,
  };
}

export const lineState = (r: ResolvedLine, imported: boolean): LineState =>
  imported ? 'imported' : r.link_to || r.missing.length === 0 ? 'ready' : 'needs';

const listWords = (words: string[]): string =>
  words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

export const needsText = (missing: RequiredField[]): string => `Needs a pick: ${listWords(missing)}`;

const teamOf = (r: ResolvedLine): Partial<Record<TeamRole, string>> =>
  Object.fromEntries(TEAM_ROLES.filter((role) => r.team[role].value).map((role) => [role, r.team[role].value as string]));

/** A draw is sent only where the API runs one: a Shows-channel project with nobody picked. */
export const drawApplies = (r: ResolvedLine): boolean =>
  r.channel.value === 'shows' && Object.keys(teamOf(r)).length === 0;

// ── Subtasks ─────────────────────────────────────────────────────────────────

/**
 * The subtasks in tree order: each parent before its children, siblings in Asana's order. The
 * API lists them in file order, where a child can come before its parent.
 */
export function treeOrder(subtasks: AsanaSubtask[]): AsanaSubtask[] {
  const gids = new Set(subtasks.map((s) => s.gid));
  const kids = new Map<string | null, AsanaSubtask[]>();
  for (const s of subtasks) {
    const parent = s.parent_gid && gids.has(s.parent_gid) && s.parent_gid !== s.gid ? s.parent_gid : null;
    kids.set(parent, [...(kids.get(parent) || []), s]);
  }
  const out: AsanaSubtask[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null) => {
    for (const s of kids.get(parent) || []) {
      if (seen.has(s.gid)) continue;
      seen.add(s.gid);
      out.push(s);
      walk(s.gid);
    }
  };
  walk(null);
  // A loop of parents never reaches the root; keep those rows rather than lose them.
  for (const s of subtasks) if (!seen.has(s.gid)) { seen.add(s.gid); out.push(s); }
  return out;
}

/** Deliverable at the first level, subtask at the second, left out deeper or by name. */
export const defaultKind = (s: AsanaSubtask, eff: EffectiveMapping): TaskKind => {
  if (eff.leaveOut.has(s.name)) return 'out';
  if (s.depth <= 1) return 'deliverable';
  if (s.depth === 2) return 'subtask';
  return 'out';
};

/**
 * What each subtask is sent as. Spark nests one level: a Subtask hangs under its nearest kept
 * ancestor when that ancestor goes in as a deliverable, and otherwise goes in as a deliverable.
 */
export function planTasks(line: AsanaLine, draft: LineDraft | undefined, eff: EffectiveMapping): PlannedTask[] {
  const chosen: Record<string, TaskKind> = draft && typeof draft.tasks === 'object' && draft.tasks ? draft.tasks : {};
  const byGid = new Map(line.subtasks.map((s) => [s.gid, s]));
  const sent = new Map<string, 'deliverable' | 'subtask' | 'out'>();
  return treeOrder(line.subtasks).map((s): PlannedTask => {
    const own = has(chosen, s.gid) && ['deliverable', 'subtask', 'out'].includes(chosen[s.gid]);
    const kind: TaskKind = own ? chosen[s.gid] : defaultKind(s, eff);
    const plan: PlannedTask = { gid: s.gid, depth: s.depth, kind, chosen: own, promoted: null, parent_gid: null };
    if (kind === 'out') {
      sent.set(s.gid, 'out');
      return plan;
    }
    if (kind === 'deliverable') {
      sent.set(s.gid, 'deliverable');
      return plan;
    }
    let up = s.parent_gid ? byGid.get(s.parent_gid) : undefined;
    const walked = new Set<string>();
    while (up && sent.get(up.gid) === 'out' && !walked.has(up.gid)) {
      walked.add(up.gid);
      up = up.parent_gid ? byGid.get(up.parent_gid) : undefined;
    }
    if (up && sent.get(up.gid) === 'deliverable') {
      sent.set(s.gid, 'subtask');
      return { ...plan, parent_gid: up.gid };
    }
    sent.set(s.gid, 'deliverable');
    return { ...plan, promoted: up && sent.get(up.gid) === 'subtask' ? { reason: 'nested', under: up.name } : { reason: 'none_kept' } };
  });
}

/** Spark keeps a task name to 200 characters. */
export const taskName = (s: AsanaSubtask): string => s.name.trim().slice(0, NAME_MAX);

// ── The request ──────────────────────────────────────────────────────────────

export function lineBody(line: AsanaLine, r: ResolvedLine, plan: PlannedTask[], eff: EffectiveMapping): AsanaLineImport {
  if (r.link_to) return { gid: line.gid, link_to: r.link_to };
  const speciality = r.speciality.value as Speciality;
  const team = teamOf(r);
  const byGid = new Map(line.subtasks.map((s) => [s.gid, s]));
  const tasks = plan.filter((p) => p.kind !== 'out').map((p): AsanaTaskImport => {
    const s = byGid.get(p.gid) as AsanaSubtask;
    const key = personKey(s.assignee);
    return {
      gid: s.gid,
      name: taskName(s),
      parent_gid: p.parent_gid,
      assignee_id: key ? eff.people[key] ?? null : null,
      due_date: s.due_date,
      status: s.completed ? 'ready' : 'not_started',
      note: s.notes,
    };
  });
  const client = (r.client_name.value || '').trim();
  return {
    gid: line.gid,
    project: {
      name: r.name.value.trim(),
      channel: r.channel.value,
      speciality,
      // Steps only shape Systems and Marketing deliverables; the API ignores them on Shows.
      ...(speciality === 'shows' ? {} : { work_steps: r.work_steps.value.filter((w) => WORK_STEPS.includes(w)) }),
      stage: r.stage.value,
      status_reason: r.status_reason.value,
      spice: r.spice.value,
      show_date: r.show_date.value || null,
      preview_due: r.preview_due.value || null,
      ls_drone_count: r.ls_drone_count.value,
      pyro_drone_count: r.pyro_drone_count.value,
      client_name: client || null,
    },
    ...(Object.keys(team).length ? { team } : {}),
    ...(r.event_id ? { event_id: r.event_id } : {}),
    ...(r.draw && drawApplies(r) ? { draw: true } : {}),
    tasks,
  };
}

// ── People ───────────────────────────────────────────────────────────────────

/** Spark's people, plus anyone the export matched by email who cannot open Spark. */
export function pickPeople(spark: Person[] | null, preview: AsanaPreview | null): PickPerson[] {
  const out = new Map<string, PickPerson>();
  for (const p of spark || []) {
    out.set(p.id, {
      id: p.id, name: p.name, roles: p.roles || [], producer: !!p.producer, pilot: !!p.pilot, studio: !!p.studio, active: true, inSpark: true,
    });
  }
  for (const v of preview?.values.people || []) {
    if (v.user && !out.has(v.user.id)) {
      out.set(v.user.id, {
        id: v.user.id, name: v.user.name || v.name || 'Someone', roles: [], producer: false, pilot: false, studio: false, active: v.user.active, inSpark: false,
      });
    }
  }
  return Array.from(out.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Subtask names that repeat at least REPEAT_AT times across the export, most first. */
export function repeatedNames(lines: AsanaLine[]): { name: string; count: number }[] {
  const n = new Map<string, number>();
  for (const l of lines) for (const s of l.subtasks) n.set(s.name, (n.get(s.name) || 0) + 1);
  return Array.from(n.entries())
    .filter(([, c]) => c >= REPEAT_AT)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

// ── Drafts in the browser ────────────────────────────────────────────────────

const STORE = 'spark.asanaImport.v1';

const record = (v: unknown): Record<string, any> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, any> : {});

/** The saved mapping and per-line changes, or null. Never throws: storage may be off or full. */
export function loadDraft(): ImportDraft | null {
  try {
    const raw = window.localStorage.getItem(STORE);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || d.version !== 1) return null;
    const m = record(d.mapping);
    return {
      version: 1,
      mapping: {
        pipeline: record(m.pipeline),
        status: record(m.status),
        spice: record(m.spice),
        speciality: record(m.speciality),
        noSpeciality: m.noSpeciality ?? null,
        people: record(m.people),
        leaveOut: Array.isArray(m.leaveOut) ? m.leaveOut.filter((x: unknown) => typeof x === 'string') : [],
      },
      lines: record(d.lines),
    };
  } catch {
    return null;
  }
}

export function saveDraft(draft: ImportDraft): void {
  try {
    window.localStorage.setItem(STORE, JSON.stringify(draft));
  } catch {
    // Storage full or blocked: the screen keeps working, only the reload memory is lost.
  }
}

export function forgetDraft(): void {
  try {
    window.localStorage.removeItem(STORE);
  } catch {
    // As above.
  }
}

export const draftIsEmpty = (d: ImportDraft | null): boolean => {
  if (!d) return true;
  const m = d.mapping;
  return Object.keys(d.lines).length === 0 && m.leaveOut.length === 0 && !m.noSpeciality
    && [m.pipeline, m.status, m.spice, m.speciality, m.people].every((x) => Object.keys(x).length === 0);
};

/** A line draft with nothing left in it. */
export const lineDraftIsEmpty = (d: LineDraft): boolean =>
  Object.entries(d).every(([k, v]) => v === undefined || ((k === 'tasks' || k === 'team') && Object.keys(record(v)).length === 0));
